import { QueryClient } from 'react-query';

import { ICliJob } from 'cli/types';
import { GetOneResponse } from 'core/types';
import { IBaseQuote, IQuoteExtraData, IQuotePendingCliJobs } from 'quotes/types';

// The versioning jobs the quote details answer reports while they are queued or running.
export enum VersioningCliJob {
  INIT_DRAFT = 'initDraft',
  RELEASE_DRAFT = 'releaseDraft',
  RESET_DRAFT = 'resetDraft',
}

type QuoteDetails = GetOneResponse<IBaseQuote, IQuoteExtraData>;

interface IQuoteDetailsQuery {
  data?: QuoteDetails;
  isError: boolean;
}

// Undefined when the details answer could not be read: the job's state is still unknown.
export const getPendingVersioningCliJob = (
  quoteQueries: IQuoteDetailsQuery[],
  quoteId: number,
  job: VersioningCliJob
): ICliJob | null | undefined => {
  const query = quoteQueries.find(({ data }) => data?.record?.id === quoteId);

  if (!query || query.isError) {
    return undefined;
  }

  const pendingCliJobs: IQuotePendingCliJobs = query.data?.extraData?.pendingCliJobs ?? {};

  return pendingCliJobs[job] ?? null;
};

// Refetches the quote details and lines, then reads the job from the fresh details answer.
export const refetchPendingVersioningCliJob = async (
  queryClient: QueryClient,
  quoteId: number,
  job: VersioningCliJob
): Promise<ICliJob | null | undefined> => {
  await Promise.all([
    queryClient.invalidateQueries('quote'),
    queryClient.invalidateQueries('customerQuoteParts'),
  ]);

  const quoteQueries = queryClient
    .getQueriesData<QuoteDetails>('quote')
    .map(([queryKey, data]) => ({
      data,
      isError: 'error' === queryClient.getQueryState(queryKey)?.status,
    }));

  return getPendingVersioningCliJob(quoteQueries, quoteId, job);
};
